import { isMultiPolygon, isPoint } from '/@/utils';

// Type definitions for geometry
// Coordinate: [lng, lat]
export type Coordinate = [number, number];

// Point: [[lng, lat]] - single coordinate wrapped in array
export type Point = [Coordinate];

// OldPolygon: [[lng, lat], [lng, lat], ...] - legacy format (flat array of coordinates)
export type OldPolygon = Coordinate[];

// Polygon: [[[lng, lat], ...], [[lng, lat], ...], ...] - polygon with optional holes
// First element is outer ring, subsequent elements are holes
export type Polygon = Coordinate[][];

// MultiPolygon: [[[[lng, lat], ...], ...], [[[lng, lat], ...], ...], ...] - array of polygons
export type MultiPolygon = Polygon[];

export type Geometry = Polygon | MultiPolygon | Point;

// Type for formatted geometry with the same nesting shape
export type FormattedGeometry = google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][];

export const formatPosition = (position: Coordinate): google.maps.LatLngLiteral => ({
    lng: Number(position[0]),
    lat: Number(position[1]),
});

// Type guard: is this a single coordinate [lng, lat]?
const isCoordinate = (value: unknown): value is Coordinate =>
    Array.isArray(value) && value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number';

/**
 * Recursively format geometry using formatPosition.
 * It will preserve the shape:
 * - Point:            [lng, lat]
 * - LineString:       [[lng, lat], ...]
 * - Polygon:          [[[lng, lat], ...], ...]
 */
export const formatGeometry = (geometry: Polygon | null | undefined): FormattedGeometry | [] => {
    if (!geometry) return [];

    const recurse = (node: unknown): any => {
        if (isCoordinate(node)) {
            // Leaf node: convert coordinate
            return formatPosition(node);
        }

        if (Array.isArray(node)) {
            // Nested array: map recursively
            return node.map(recurse);
        }

        // Anything unexpected -> return as-is (or you could throw)
        return node;
    };

    return recurse(geometry);
};

// Helper function to flatten multipolygon to single polygon coordinates
export const flattenGeometry = (geometry: Geometry): Coordinate[] => {
    if (isMultiPolygon(geometry)) {
        // MultiPolygon: flatten all polygons
        return geometry.flat().flat();
    }

    if (isPoint(geometry)) {
        return geometry;
    }

    // Single Polygon
    return geometry.flat();
};

export const getBounds = (geometry: Geometry, padding: Record<string, number> = {}) => {
    const bounds = new google.maps.LatLngBounds();

    if (!geometry || !geometry.length) return bounds;

    // Handle both polygon and multipolygon by flattening if needed
    const points = flattenGeometry(geometry);

    for (const point of points) {
        bounds.extend(formatPosition(point));
    }

    ['top', 'right', 'bottom', 'left'].forEach((side) => {
        const paddingToAdd = padding[side];

        if (!paddingToAdd) return;
        let maxReducer: (
            max: google.maps.LatLngLiteral,
            current: google.maps.LatLngLiteral
        ) => google.maps.LatLngLiteral = () => ({ lng: 0, lat: 0 });
        let increaseFunction: (pos: google.maps.LatLngLiteral) => google.maps.LatLngLiteral = (pos) => pos;

        const getSign = (num: number): number => (Math.abs(num) / num > 0 ? 1 : -1);

        const absAdd = (a: number, b: number): number => {
            if (getSign(a) === getSign(b)) return getSign(a) * (Math.abs(a) + Math.abs(b));
            return a + b;
        };

        const absSub = (a: number, b: number): number => {
            if (getSign(a) === getSign(b)) return (a / a) * (Math.abs(a) - Math.abs(b));
            return a - b;
        };

        switch (side) {
            case 'top': {
                maxReducer = (max, current) => (Math.abs(current.lng) >= Math.abs(max.lng) ? current : max);
                increaseFunction = ({ lng, lat }) => ({ lng, lat: absAdd(lat, paddingToAdd) });
                break;
            }

            case 'left': {
                maxReducer = (max, current) => (Math.abs(current.lat) < Math.abs(max.lat) ? current : max);
                increaseFunction = ({ lng, lat }) => ({ lng, lat: absSub(lat, paddingToAdd) });
                break;
            }

            case 'right': {
                maxReducer = (max, current) => (Math.abs(current.lat) >= Math.abs(max.lat) ? current : max);
                increaseFunction = ({ lng, lat }) => ({ lng, lat: absAdd(lat, paddingToAdd) });
                break;
            }

            case 'bottom': {
                maxReducer = (max, current) => (Math.abs(current.lng) < Math.abs(max.lng) ? current : max);
                increaseFunction = ({ lng, lat }) => ({ lat, lng: absSub(lng, paddingToAdd) });
                break;
            }
        }

        const max = points.map(formatPosition).reduce(maxReducer);
        const increased = increaseFunction(max);
        bounds.extend(increased);
    });
    return bounds;
};

export const getCenterOfGeometry = (geometry: Geometry): [number, number] => {
    // Handle single point
    if (isPoint(geometry)) {
        return geometry[0] as [number, number];
    }

    // Handle polygon or multipolygon
    const bounds = getBounds(geometry);
    if (!bounds) return [0, 0];
    const center = bounds.getCenter();
    if (!center) return [0, 0];
    return [center.lng(), center.lat()];
};

export const getCenterOfBounds = (bounds: { south: number; north: number; west: number; east: number }) => {
    const lat = bounds.south + (bounds.north - bounds.south) / 2;
    const lng = bounds.west + (bounds.east - bounds.west) / 2;
    return [lng, lat];
};
